import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isCurrencyCode } from 'pi-kiosk-shared';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { ApiError } from '../../shared/api/ApiClient.js';
import { hasCustomerApiTenant } from '../../shared/tenant/customerApiTenant.js';
import { useCustomerAnalytics } from '../../shared/analytics/CustomerAnalyticsProvider.js';
import type { AnalyticsEventName } from 'pi-kiosk-shared/analyticsEvents';
import { BuyAgainTrimDialog } from '../checkout/BuyAgainTrimDialog.js';
import {
  useAccountCheckoutApi,
  useBuyAgainSwitchGate,
  type BuyAgainAvailability,
  type BuyAgainStartResult,
} from './accountCheckoutApi.js';
import { resolveBuyAgainError } from './resolveBuyAgainError.js';

type BuyAgainSource =
  | { kind: 'order'; transactionId: number; shopTenantCode?: string; currency?: string }
  | { kind: 'session'; sessionId: string; shopTenantCode?: string; currency?: string };

/** Empty-trim sentinel when BE throws BUY_AGAIN_NO_STOCK (no invented line ids). */
const ALL_REMOVED_TRIM: BuyAgainAvailability = {
  availableLines: [],
  unavailableLines: [],
};

/**
 * Order buy-again requires prior currency on the card (G8 fail-closed).
 * Session resume may omit — BE resolves from session currency.
 */
function requireOrderBuyAgainCurrency(source: BuyAgainSource): string | undefined {
  if (source.kind !== 'order') {
    return isCurrencyCode(source.currency) ? source.currency : undefined;
  }
  if (!isCurrencyCode(source.currency)) {
    throw new ApiError(
      'Buy-again currency is required (fail-closed; no silent CZK default)',
      0,
      'CURRENCY_NOT_ALLOWED',
    );
  }
  return source.currency;
}

/** Stock/empty-cart failures that should open empty-trim (not error banner). */
function isBuyAgainStockTrimError(err: unknown): boolean {
  if (!(err instanceof ApiError)) {
    return false;
  }
  return (
    err.code === 'BUY_AGAIN_NO_STOCK' ||
    err.code === 'PRODUCT_CHECKOUT_REQUIRES_SHOP_CART' ||
    err.code === 'INSUFFICIENT_INVENTORY'
  );
}

function resolveCheckoutTenant(
  urlTenantCode: string,
  shopTenantCode: string | undefined,
  resultTenantCode?: string,
): string | null {
  const shop = shopTenantCode?.trim() ?? '';
  if (hasCustomerApiTenant(shop)) {
    return shop;
  }
  const fromResult = resultTenantCode?.trim() ?? '';
  if (hasCustomerApiTenant(fromResult)) {
    return fromResult;
  }
  if (hasCustomerApiTenant(urlTenantCode)) {
    return urlTenantCode.trim();
  }
  return null;
}

export function useBuyAgainFlow(): {
  startBuyAgain: (source: BuyAgainSource) => Promise<void>;
  trimDialog: JSX.Element | null;
  isPending: boolean;
  pendingTransactionId: number | null;
  error: Error | null;
  errorTransactionId: number | null;
  errorSessionId: string | null;
} {
  const tenantCode = useTenantCode();
  const navigate = useNavigate();
  const { t } = useTranslation('customer');
  const api = useAccountCheckoutApi();
  const ensureBuyAgainAuth = useBuyAgainSwitchGate();
  const analytics = useCustomerAnalytics();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [pendingTransactionId, setPendingTransactionId] = useState<number | null>(null);
  const [errorTransactionId, setErrorTransactionId] = useState<number | null>(null);
  const [errorSessionId, setErrorSessionId] = useState<string | null>(null);
  const [trimAvailability, setTrimAvailability] = useState<BuyAgainAvailability | null>(null);
  const [pendingSource, setPendingSource] = useState<BuyAgainSource | null>(null);

  const requestContext = (source: BuyAgainSource) => {
    const currency = requireOrderBuyAgainCurrency(source);
    return {
      ...(source.shopTenantCode !== undefined ? { shopTenantCode: source.shopTenantCode } : {}),
      ...(currency !== undefined ? { currency } : {}),
    };
  };

  const navigateToSession = useCallback(
    (result: BuyAgainStartResult, source: BuyAgainSource) => {
      const checkoutTenant = resolveCheckoutTenant(
        tenantCode,
        source.shopTenantCode,
        result.tenantCode,
      );
      if (checkoutTenant === null) {
        // Hub / empty-tenant: never imply "open the shop" while already on the order.
        throw new ApiError(
          t('account.orders.buyAgainFailed'),
          0,
          'BUY_AGAIN_MISSING_SHOP_TENANT',
        );
      }
      navigate(
        `/${encodeURIComponent(checkoutTenant)}/checkout?sessionId=${encodeURIComponent(result.newSessionId)}`,
      );
    },
    [navigate, t, tenantCode],
  );

  const activeOrderId = (source: BuyAgainSource): number | null =>
    source.kind === 'order' ? source.transactionId : null;

  const activeSessionId = (source: BuyAgainSource): string | null =>
    source.kind === 'session' ? source.sessionId : null;

  const clearBuyAgainErrors = useCallback((): void => {
    setError(null);
    setErrorTransactionId(null);
    setErrorSessionId(null);
  }, []);

  const emitBuyAgainNoStock = useCallback(
    (source: BuyAgainSource): void => {
      analytics.track({
        eventName: 'buy_again_failed_stock' as AnalyticsEventName,
        flow: 'RETAIL',
        screenName: 'account_orders',
        metadata: {
          source: source.kind === 'order' ? 'ORDER' : 'INCOMPLETE_SESSION',
          ...(source.kind === 'order'
            ? { transactionId: source.transactionId }
            : { checkoutSessionId: source.sessionId }),
        },
      });
    },
    [analytics],
  );

  const openTrim = useCallback((availability: BuyAgainAvailability, source: BuyAgainSource) => {
    setTrimAvailability(availability);
    setPendingSource(source);
  }, []);

  const runStart = useCallback(
    async (source: BuyAgainSource): Promise<void> => {
      setPending(true);
      setPendingTransactionId(activeOrderId(source));
      clearBuyAgainErrors();
      let openedStockTrim = false;
      try {
        await ensureBuyAgainAuth();
        const context = requestContext(source);
        const result =
          source.kind === 'order'
            ? await api.startOrderBuyAgain(source.transactionId, context)
            : await api.startSessionBuyAgain(source.sessionId, context);
        // `buy_again_started` is server-emitted by StartBuyAgainCheckoutUseCase —
        // see docs/ANALYTICS_SERVER_EMITTED_EVENTS.md.
        navigateToSession(result, source);
      } catch (err) {
        if (isBuyAgainStockTrimError(err)) {
          emitBuyAgainNoStock(source);
          openTrim(ALL_REMOVED_TRIM, source);
          openedStockTrim = true;
          return;
        }
        setError(resolveBuyAgainError(err, t('account.orders.buyAgainFailed')));
        setErrorTransactionId(activeOrderId(source));
        setErrorSessionId(activeSessionId(source));
      } finally {
        setPending(false);
        setPendingTransactionId(null);
        if (!openedStockTrim) {
          setTrimAvailability(null);
          setPendingSource(null);
        }
      }
    },
    [api, clearBuyAgainErrors, emitBuyAgainNoStock, ensureBuyAgainAuth, navigateToSession, openTrim, t],
  );

  const startBuyAgain = useCallback(
    async (source: BuyAgainSource): Promise<void> => {
      clearBuyAgainErrors();
      setPending(true);
      setPendingTransactionId(activeOrderId(source));
      try {
        await ensureBuyAgainAuth();
        const context = requestContext(source);
        const availability =
          source.kind === 'order'
            ? await api.validateOrderBuyAgain(source.transactionId, context)
            : await api.validateSessionBuyAgain(source.sessionId, context);
        // Trim only on 200 + unavailableLines (incl. availableLines=[] all-miss).
        if (availability.unavailableLines.length > 0) {
          if (availability.availableLines.length === 0) {
            emitBuyAgainNoStock(source);
          }
          openTrim(availability, source);
          return;
        }
        await runStart(source);
      } catch (err) {
        // Option c: map BUY_AGAIN_NO_STOCK to empty-trim UX (not error banner).
        if (err instanceof ApiError && err.code === 'BUY_AGAIN_NO_STOCK') {
          emitBuyAgainNoStock(source);
          openTrim(ALL_REMOVED_TRIM, source);
          return;
        }
        setError(resolveBuyAgainError(err, t('account.orders.buyAgainFailed')));
        setErrorTransactionId(activeOrderId(source));
        setErrorSessionId(activeSessionId(source));
      } finally {
        setPending(false);
        setPendingTransactionId(null);
      }
    },
    [api, clearBuyAgainErrors, emitBuyAgainNoStock, ensureBuyAgainAuth, openTrim, runStart, t],
  );

  const canContinueTrim =
    trimAvailability !== null && trimAvailability.availableLines.length > 0;

  const trimDialog =
    trimAvailability !== null && pendingSource !== null ? (
      <BuyAgainTrimDialog
        removedCount={trimAvailability.unavailableLines.length}
        pending={pending}
        canContinue={canContinueTrim}
        onCancel={() => {
          setTrimAvailability(null);
          setPendingSource(null);
        }}
        onConfirm={() => {
          // Never start with an empty cart (all-removed / NO_STOCK empty-trim).
          if (pendingSource !== null && canContinueTrim) {
            void runStart(pendingSource);
          }
        }}
      />
    ) : null;

  return {
    startBuyAgain,
    trimDialog,
    isPending: pending,
    pendingTransactionId,
    error,
    errorTransactionId,
    errorSessionId,
  };
}
